import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { Lock, Mail, LogIn } from "lucide-react";

export default function Login() {
  const { login, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  if (user) {
    const from = location.state?.from || "/";
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const r = await login(email.trim(), password);
    if (r.ok) {
      const from = location.state?.from || "/";
      navigate(from, { replace: true });
    } else {
      setError(r.error);
    }
  };

  return (
    <div
      className="min-h-screen flex items-stretch"
      style={{ background: "var(--ifl-bg)" }}
      data-testid="login-page"
    >
      {/* Left — editorial brand panel */}
      <aside
        className="hidden lg:flex flex-col justify-between w-[44%] px-16 py-12 border-r"
        style={{ borderColor: "var(--ifl-border)" }}
      >
        <div className="ifl-fade-up">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: "var(--ifl-terracotta)" }} />
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: "var(--ifl-mustard)" }} />
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: "var(--ifl-success)" }} />
          </div>
          <div className="heading-caps text-sm" style={{ letterSpacing: "0.22em" }}>
            INDIE FOOD LAB
          </div>
          <div className="font-italic-serif text-2xl mt-1" style={{ color: "var(--ifl-text)" }}>
            custos <span style={{ color: "var(--ifl-muted)" }}>&amp;</span> insumos.
          </div>
        </div>

        <div className="ifl-fade-up" style={{ animationDelay: "120ms" }}>
          <div className="label-caps mb-3">Sistema interno</div>
          <div
            className="font-display"
            style={{ fontSize: "2.4rem", lineHeight: 1.05, letterSpacing: "-0.01em", color: "var(--ifl-text)" }}
          >
            CONTROLE DE
            <br />
            <span className="font-italic-serif" style={{ color: "var(--ifl-terracotta)" }}>cozinha</span>
            <br />
            EM TEMPO REAL.
          </div>
          <div className="font-body text-sm mt-6 max-w-md" style={{ color: "var(--ifl-muted)" }}>
            Acesso restrito à equipe Indie. Os dados sincronizam com Omie e PDV Legal — não compartilhe credenciais.
          </div>
        </div>

        <div className="font-body text-xs" style={{ color: "var(--ifl-muted)" }}>
          © {new Date().getFullYear()} Indie Food Lab
        </div>
      </aside>

      {/* Right — form */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <form
          onSubmit={submit}
          className="w-full max-w-md ifl-fade-up"
          style={{ animationDelay: "80ms" }}
          data-testid="login-form"
        >
          <div className="lg:hidden mb-8 text-center">
            <div className="heading-caps text-sm" style={{ letterSpacing: "0.22em" }}>INDIE FOOD LAB</div>
            <div className="font-italic-serif text-xl mt-1">custos &amp; insumos.</div>
          </div>

          <div className="label-caps mb-2">Entrar</div>
          <h1 className="font-display text-4xl mb-2" style={{ letterSpacing: "-0.01em" }}>
            ACESSO À<br />
            <span className="font-italic-serif" style={{ color: "var(--ifl-terracotta)" }}>plataforma.</span>
          </h1>
          <p className="font-body text-sm mb-8" style={{ color: "var(--ifl-muted)" }}>
            Use seu e-mail corporativo e a senha fornecida pelo administrador.
          </p>

          <div className="mb-4">
            <label className="label-caps block mb-2" htmlFor="email">E-mail</label>
            <div className="relative">
              <Mail
                size={14}
                strokeWidth={1.5}
                style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--ifl-muted)" }}
              />
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@indiefoodlab.com.br"
                className="ifl-input w-full"
                style={{ paddingLeft: 38 }}
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="label-caps block mb-2" htmlFor="password">Senha</label>
            <div className="relative">
              <Lock
                size={14}
                strokeWidth={1.5}
                style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--ifl-muted)" }}
              />
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="ifl-input w-full"
                style={{ paddingLeft: 38 }}
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <div
              className="mb-4 px-4 py-3 rounded-xl border font-body text-sm"
              style={{ background: "#FCEFEA", borderColor: "var(--ifl-terracotta)", color: "var(--ifl-terracotta)" }}
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="ifl-btn w-full justify-center"
            disabled={loading}
            data-testid="login-submit-btn"
          >
            <LogIn size={14} strokeWidth={2} className={loading ? "ifl-pulse" : ""} />
            {loading ? "Entrando…" : "Entrar"}
          </button>

          <div className="font-body text-xs mt-8" style={{ color: "var(--ifl-muted)" }}>
            Esqueceu a senha? Solicite ao admin um reset manual em <code style={{ color: "var(--ifl-text)" }}>Configurações → Usuários</code>.
          </div>
        </form>
      </main>
    </div>
  );
}
